// @flow
import * as React from 'react';
import { DateTime } from 'luxon';
import { fetchApplications } from 'api/ats';
import type { Listing, Org } from 'api/orgAdmin';
import type { Org as StaffOrg } from 'api/staff/orgs';
import type { Group } from 'api/groupAdmin';
import type { ListingApplicationMini, ListingApplicationType } from 'api/ats';
import { configValue } from 'utils/config';
import scrollIntoView from 'utils/scrollIntoView';
import * as localStorage from 'utils/localStorage';
import {
  getNotificationStatus,
  setNotificationStatus,
  notificationIsDismissed,
  dismissNotification,
  NOTIFICATIONS
} from 'utils/localNotifications';

const { useEffect, useState, useCallback } = React;

type ApplicationSearch = (
  string,
  ?string,
  ListingApplicationType,
  ?number
) => {
  loading: boolean,
  applications: ?Array<ListingApplicationMini>,
  count: ?number,
  pages: ?number,
  pageIndex: number,
  perPage: ?number,
  setPageIndex: number => void,
  search: Function,
  searchIndex: number
};

export const useApplicationSearch: ApplicationSearch = (
  orgId,
  listingId,
  listingType,
  initialIndex
) => {
  const [loading, setLoading] = useState(true);
  const [pageIndex, updatePageIndex] = useState(initialIndex || 0);
  const [applications, setApplications] = useState();
  const [count, setCount] = useState();
  const [pages, setPages] = useState();
  const [perPage, setPerPage] = useState();
  const [searchIndex, setSearchIndex] = useState(0);

  const setPageIndex = (index: number) => {
    updatePageIndex(index);
    scrollIntoView();
  };

  const search = useCallback(
    () => {
      const params = {
        id: listingId,
        page: pageIndex + 1,
        type: listingType
      };

      fetchApplications(orgId, params).then(response => {
        setApplications(response.listingApplications);
        setCount(response.count);
        setPages(response.lastPage);
        setPerPage(response.perPage);
        setLoading(false);
        setSearchIndex(prev => prev + 1);
      });
    },
    [listingId, listingType, orgId, pageIndex]
  );

  useEffect(
    () => {
      search();
    },
    [listingId, listingType, orgId, pageIndex, search]
  );

  return {
    loading,
    applications,
    count,
    pages,
    pageIndex,
    perPage,
    setPageIndex,
    search,
    searchIndex
  };
};

export const atsSurveyBannerIsDismissed = () =>
  notificationIsDismissed(NOTIFICATIONS.ORG_ADMIN_ATS_SURVEY_BANNER);

export const atsSurveyBannerShouldDisplay = () =>
  getNotificationStatus(NOTIFICATIONS.ORG_ADMIN_ATS_SURVEY_BANNER) === 'NEW';

export const dismissAtsSurveyBanner = () =>
  dismissNotification(NOTIFICATIONS.ORG_ADMIN_ATS_SURVEY_BANNER);

export const showAtsSurveyBanner = () => {
  if (!atsSurveyBannerIsDismissed()) {
    setNotificationStatus(NOTIFICATIONS.ORG_ADMIN_ATS_SURVEY_BANNER, 'NEW');
  }
};

export const getDefaultApplyAckText = (
  org: Org | StaffOrg | Group,
  listing?: Listing
) => {
  const locale = (listing && listing.locale) || org.locale;
  const listingName = (listing && listing.name) || '<p><br></p>';

  const orgName: string =
    listing && listing.hasOrgNameOverride && listing.orgNameOverride
      ? listing.orgNameOverride
      : org.name
      ? org.name
      : '';

  const showOrgName =
    (listing && !listing.isPostedAnonymously && orgName) || false;

  return {
    en: `<p>Your resume and application materials have been successfully submitted ${
      showOrgName ? `to ${orgName} ` : ''
    }for the following listing.</p>${listingName}<p>Best Regards,</p><p>The Idealist Support Team</p>`,

    es: `<p>Tu currículum y otros materiales para tu postulación se han enviado correctamente ${
      showOrgName ? `a ${orgName} ` : ''
    }para la siguiente oportunidad.</p>${listingName}<p>Suerte,</p><p>El Equipo de Idealist</p>`,

    pt: `<p>O seu currículo e material de inscrição foram submetidos com sucesso ${
      showOrgName ? `para ${orgName} ` : ''
    }para o seguinte anúncio.</p>${listingName}<p>Saudações,</p><p>Equipe de Apoio do Idealist</p>`
  }[locale];
};

const HAS_USED_ATS = 'hasUsedAts';

export const usedAts = () => localStorage.setItem(HAS_USED_ATS, true);

export const hasUsedAts = () => localStorage.getItem(HAS_USED_ATS);
export const hasLegacyStatuses = (listing: Object) => {
  const firstPublished = listing.firstPublished;
  const newStatusStartDate = configValue(
    'idealist',
    'ats',
    'newStatusStartDate'
  );

  if (!(firstPublished && newStatusStartDate)) {
    return false;
  }

  const firstPublishedDT = DateTime.fromISO(firstPublished);
  const newStatusStartDateDT = DateTime.fromISO(newStatusStartDate);

  return firstPublishedDT < newStatusStartDateDT;
};
